/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable camelcase */
import create from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  InventoryMeter,
  InventoryMeterMountType,
  SerializedInventoryMeter,
  SerializedManufacturer,
  SerializedMeterModel,
  CreateInventoryMeterOutputDto,
} from '@enmon/commons/lib/tools';
import { UploadFile } from 'antd';
import { FullOBIS, SerializedMeter, createFullOBIS, isFullOBIS } from '@enmon/commons/lib/enmon';
import { RcFile } from 'antd/lib/upload';
import { isConsumptionRegister, Nullable, SerializedMeterRegister } from '@enmon/commons';
import { createDetailsStepSlice, DetailsStepForm, DetailsStepSlice } from './steps/details/slice';
import { createNewValuesStepSlice, NewValuesStepForm, NewValuesStepSlice } from './steps/new_values/slice';
import { createOldValuesStepSlice, OldValuesStepForm, OldValuesStepSlice } from './steps/old_values/slice';
import { createReviewStepSlice, ReviewStepSlice } from './steps/review/slice';

export type RegisterState = Pick<SerializedMeterRegister, '_id' | 'name' | 'unit'> & {
  fullObis?: string;
  counter?: number;
  photos?: UploadFile[];
};

type ReplaceMeterForm = OldValuesStepForm & NewValuesStepForm & DetailsStepForm;

interface BaseStoreValues {
  isSendingData?: boolean;
  queuedMetersCount?: number;
}

export type ReplaceMeterStore = OldValuesStepSlice &
  NewValuesStepSlice &
  DetailsStepSlice &
  ReviewStepSlice &
  BaseStoreValues & {
    setQueuedMetersCount: (payload: number) => void;
    setIsSendingData: (isSendingData: boolean) => void;
    reset: () => void;
    resetBaseValues: () => void;
    setForm: (values: ReplaceMeterForm) => void;
  };

const INITIAL_STATE: BaseStoreValues = {
  queuedMetersCount: undefined,
  isSendingData: false,
};

/** Store for Install Meter module */
export const useReplaceMeterStore = create<ReplaceMeterStore>(
  devtools(
    (set, get) => ({
      ...INITIAL_STATE,

      ...createOldValuesStepSlice(set),
      ...createNewValuesStepSlice(set),
      ...createDetailsStepSlice(set),
      ...createReviewStepSlice(set),

      setIsSendingData: (isSendingData: boolean) => {
        set(() => ({
          isSendingData,
        }));
      },

      setQueuedMetersCount: (count: number) => {
        set(() => ({ queuedMetersCount: count }));
      },

      resetBaseValues: () => set(INITIAL_STATE),

      reset: () => {
        get().resetOldValuesStepSlice();
        get().resetNewValuesStepSlice();
        get().resetDetailsStepSlice();
        get().resetReviewStepSlice();
        get().resetBaseValues();
      },

      setForm: (formValues: ReplaceMeterForm) => {
        set(() => formValues);
      },
    }),
    'ReplaceMeterStore',
  ),
);

export const selectReplaceMeterFormFromPayload = (
  enmonMeter: Nullable<SerializedMeter>,
  inventoryMeter: Nullable<SerializedInventoryMeter>,
): ReplaceMeterForm => {
  if (!enmonMeter || !inventoryMeter) {
    return {} as ReplaceMeterForm;
  }

  const { serial, registers } = enmonMeter;

  const { meter_mount_type, meter_mount_technology } = inventoryMeter;

  return {
    // OldValues step
    oldSerialNumber: serial,
    oldRegisterStates: (registers ?? [])
      .filter(isConsumptionRegister)
      .map(({ _id, name, unit, medium, obis, tariff }) => ({
        _id,
        name: name ?? '',
        unit,
        ...(medium && obis ? { fullObis: createFullOBIS({ medium, obis, tariff }) } : {}),
      })),

    // NewValues step
    newRegisterStates: (registers ?? [])
      .filter(isConsumptionRegister)
      .map(({ _id, name, unit, medium, obis, tariff }) => ({
        _id,
        name: name ?? '',
        unit,
        ...(medium && obis ? { fullObis: createFullOBIS({ medium, obis, tariff }) } : {}),
      })),
    outputs: [],

    // Details step
    meterMountType: meter_mount_type,
    meterMountTechnology: meter_mount_technology,
  };
};

export type SelectedReplaceMeterPayload = Pick<
  SerializedInventoryMeter,
  'internal_note' | 'meter_mount_type' | 'meter_mount_technology'
> &
  Pick<SerializedMeter, 'calibrated' | 'manufactured'> & {
    id?: SerializedInventoryMeter['id'];
    manufacturer: SerializedManufacturer['id'] | null;
    meter_model: SerializedMeterModel['id'] | null;
    replacement_date: string;
    old_serial_number: string;
    old_register_states: Array<
      Pick<SerializedMeterRegister, '_id' | 'unit'> & { counter?: number; fullObis?: FullOBIS }
    >;
    outputs: Array<CreateInventoryMeterOutputDto>;
    new_serial_number: string;
    new_register_states: Array<
      Pick<SerializedMeterRegister, '_id' | 'unit'> & { counter?: number; fullObis?: FullOBIS }
    >;
    detail: Partial<SerializedMeter['detail']>;
    replaced_by?: string | null;
    commodity?: SerializedMeter['commodity'];
  };

export const selectReplaceMeterPayload = ({
  manufacturer,
  meterModel,
  calibrated,
  manufactured,
  replacementDate,
  oldSerialNumber,
  oldRegisterStates,
  newSerialNumber,
  newRegisterStates,
  detail,
  internalNote,
  meterMountType,
  meterMountTechnology,
  outputs,
  commodity,
}: ReplaceMeterStore): SelectedReplaceMeterPayload => ({
  manufacturer: manufacturer ?? null,
  meter_model: meterModel ?? null,
  calibrated: calibrated?.year() ?? null,
  manufactured: manufactured?.year() ?? null,
  internal_note: internalNote ?? null,
  replacement_date: replacementDate?.toISOString() ?? new Date().toISOString(),
  old_serial_number: oldSerialNumber ?? '',
  old_register_states:
    oldRegisterStates?.map(({ _id, unit, counter, fullObis }) => ({
      _id,
      unit,
      counter,
      fullObis: isFullOBIS(fullObis) ? fullObis : undefined,
    })) ?? [],
  new_serial_number: newSerialNumber ?? '',
  new_register_states:
    newRegisterStates?.map(({ _id, unit, counter, fullObis }) => ({
      _id,
      unit,
      counter,
      fullObis: isFullOBIS(fullObis) ? fullObis : undefined,
    })) ?? [],
  outputs: outputs ?? [],
  meter_mount_type: meterMountType ?? InventoryMeterMountType.MountedOccupied,
  meter_mount_technology: meterMountTechnology ?? null,
  commodity,

  detail: {
    ...(detail ?? {}),
    ...(detail &&
      'flowrate' in detail && {
        flowrate: `${detail?.flowrate?.unit ?? ''} ${detail.flowrate?.value ?? ''}`,
      }),

    ...(detail &&
      'width' in detail && {
        width:
          detail?.width?.unit === 'DIN' ? (detail?.width?.value ?? 0) * 17.5 || null : detail?.width?.value ?? null,
      }),
    ...(detail && 'isDirect' in detail && { isDirect: Boolean(detail.isDirect) }),
    ...(detail && 'flange' in detail && { flange: detail.flange === null ? null : Boolean(detail.flange) }),
    ...(detail && 'hotWater' in detail && { hotWater: detail.hotWater === null ? null : Boolean(detail.hotWater) }),
    ...(detail && 'woltman' in detail && { woltman: detail.woltman === null ? null : Boolean(detail.woltman) }),
  },
});

export const selectMeterModel =
  (meterModels: SerializedMeterModel[]) =>
  (state: ReplaceMeterStore): { meterModel: SerializedMeterModel | null } => ({
    meterModel: meterModels?.find(meterModel => meterModel.id === state.meterModel) ?? null,
  });

export interface SelectedReplaceMeterPhotos {
  detailPhotos: (RcFile | undefined)[];
  protocolPhotos: (RcFile | undefined)[];
  oldRegistersPhotos: Array<(RcFile | undefined)[]>;
  newRegistersPhotos: Array<(RcFile | undefined)[]>;
}

export const selectReplaceMeterPhotos = (state: ReplaceMeterStore): SelectedReplaceMeterPhotos => ({
  detailPhotos: state.detailPhotos?.map(photo => photo.originFileObj) ?? [],
  protocolPhotos: state.protocolPhotos?.map(photo => photo.originFileObj) ?? [],
  oldRegistersPhotos: state.oldRegisterStates?.map(({ photos }) => photos?.map(photo => photo.originFileObj) ?? []) ?? [
    [],
  ],
  newRegistersPhotos: state.newRegisterStates?.map(({ photos }) => photos?.map(photo => photo.originFileObj) ?? []) ?? [
    [],
  ],
});

export interface ReplaceMeterRecord {
  formPhotos: SelectedReplaceMeterPhotos;
  data: SelectedReplaceMeterPayload;
  inventoryMeterId: InventoryMeter['id'];
}
